import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import { PageContainer } from '../../components/AdminContentWrapper';
import { ADMIN_COLORS, outlinedButtonSx, panelSx } from '../../components/adminStyles';
import DashboardsService from '../../../../services/DashboardsService';

export const RUN_STATUS = {
  succeeded: { label: 'Actualizado', color: 'var(--wb-success)', bg: 'var(--wb-x-bg-22c55e29, rgba(34,197,94,0.16))' },
  partial: { label: 'Con errores', color: 'var(--wb-warning)', bg: 'var(--wb-warning-12)' },
  failed: { label: 'Falló', color: 'var(--wb-danger)', bg: 'var(--wb-danger-16)' },
  running: { label: 'Refrescando…', color: 'var(--wb-accent)', bg: 'var(--wb-accent-tint)' }
};

export const formatDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
};

export const StatusChip = ({ status }) => {
  const info = RUN_STATUS[status];
  if (!info) return null;
  return <Chip size="small" label={info.label} sx={{ color: info.color, backgroundColor: info.bg, fontWeight: 600 }} />;
};

/**
 * Mis tableros: los tableros guardados desde el modo Dashboard del chat.
 * basePath: ruta de la lista; el detalle cuelga de ella (se muestra tanto en
 * el admin como dentro del chat).
 */
const MyDashboards = ({ basePath = '/admin/home/tableros' }) => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await DashboardsService.list();
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los tableros.');
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PageContainer
      title="Mis tableros"
      description='Tableros guardados desde el modo "Dashboard" del chat. Se refrescan con datos actuales sin volver a usar la IA.'
      actions={(
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={items === null} sx={outlinedButtonSx}>
          Actualizar
        </Button>
      )}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {items === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ADMIN_COLORS.accent }} /></Box>
      ) : items.length === 0 ? (
        <Box sx={{ ...panelSx, p: 4, textAlign: 'center' }}>
          <DashboardOutlinedIcon sx={{ color: ADMIN_COLORS.muted, fontSize: 40 }} />
          <Typography sx={{ color: ADMIN_COLORS.title, fontWeight: 600, mt: 1 }}>Todavía no tienes tableros guardados</Typography>
          <Typography sx={{ color: ADMIN_COLORS.muted, mt: 0.5 }}>
            En el chat elige el modo "Dashboard", pide un tablero y pulsa "Guardar tablero".
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {items.map((item) => (
            <Grid item xs={12} sm={6} lg={4} key={item.dashboard_id}>
              <Box
                component={RouterLink}
                to={`${basePath}/${item.dashboard_id}`}
                sx={{ ...panelSx, p: 2, display: 'block', height: '100%', textDecoration: 'none', '&:hover': { borderColor: ADMIN_COLORS.accent } }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Typography sx={{ color: ADMIN_COLORS.title, fontWeight: 600 }}>{item.title}</Typography>
                  <StatusChip status={item.last_run_status} />
                </Stack>
                {item.description && (
                  <Typography variant="body2" sx={{ color: ADMIN_COLORS.muted, mt: 0.5 }}>{item.description}</Typography>
                )}
                <Typography variant="caption" sx={{ color: ADMIN_COLORS.muted, display: 'block', mt: 1.5 }}>
                  {item.tiles} mosaicos · {item.last_refreshed_at ? `actualizado ${formatDate(item.last_refreshed_at)}` : 'sin datos todavía'}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </PageContainer>
  );
};

export default MyDashboards;
